import { get, omit } from 'lodash';

import { Action } from '../actions';
import {
    CLOSE_MASS_ACTIONS_CPM_CREATIVE_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_CPM_CREATIVE_BULK_REPLACEMENT_EDITOR
} from '../actions/banners-edit/banners-edit-mass-actions';
import {
    ADD_NEW_TEXT_BANNERS,
    AddNewTextBannersAction,
    CLEAR_BANNERS,
    ClearBanners,
    COPY_BANNER,
    CopyBanner,
    DELETE_BANNERS,
    DeleteBanners,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess
} from '../actions/bannersEdit';
import {
    CLOSE_CREATIVE_CONSTRUCTOR,
    CloseCreativeConstructor,
    MASS_UPDATE_BANNER_CREATIVE_DATA,
    MassUpdateBannerCreativeData,
    OPEN_CREATIVE_CONSTRUCTOR,
    OPEN_CREATIVE_CONSTRUCTOR_PRESETS,
    OPEN_CREATIVE_HTML5_ADD_CONSTRUCTOR,
    OPEN_CREATIVE_HTML5_CONSTRUCTOR,
    OPEN_CREATIVE_IDEAS,
    OpenCreativeConstructor,
    OpenCreativeConstructorPresets,
    OpenCreativeHtml5AddConstructor,
    OpenCreativeHtml5Constructor,
    OpenCreativeIdeas,
    SET_CREATIVE_DATA,
    SetCreativeData,
    UPDATE_BANNER_CREATIVE_DATA,
    UPDATE_BANNER_CREATIVE_ID,
    UpdateBannerCreativeData,
    UpdateBannerCreativeId
} from '../actions/creative';
import { CanvasPageType } from '../components/ImageCanvasIFrame/ImageCanvasIFrame';
import { mapCreativeData } from '../mappers/creative';
import { selectCpmCreativeDataByCreativeReplace } from '../selectors/bannerEdit/massActions/massActions';
import { SaveBannersResultData } from '../typings/banners';
import { NewBannerInputData } from '../typings/bannersEditState';
import { EditorPlaceId } from '../typings/common';
import { CreativeState } from '../typings/creative';
import { State } from '../typings/state';

const openCreativeConstructor = (
    state: CreativeState,
    action: OpenCreativeConstructor
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: true
        }
    };
};

const openCreativeConstructorPresets = (
    state: CreativeState,
    action: OpenCreativeConstructorPresets
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: true,
            pageType: CanvasPageType.presets
        }
    };
};

const openCreativeIdeas = (
    state: CreativeState,
    action: OpenCreativeIdeas
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: true,
            pageType: CanvasPageType.ideas
        }
    };
};

const openCreativeHtml5Constructor = (
    state: CreativeState,
    action: OpenCreativeHtml5Constructor
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: true,
            pageType: CanvasPageType.html5
        }
    };
};

const openCreativeHtml5AddConstructor = (
    state: CreativeState,
    action: OpenCreativeHtml5AddConstructor
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: true,
            pageType: CanvasPageType.html5Add
        }
    };
};

const closeConstructor = (
    state: CreativeState,
    action: CloseCreativeConstructor
): CreativeState => {
    return {
        ...state,
        [action.bannerId]: {
            ...state[action.bannerId],
            isCanvasModalOpened: false,
            pageType: undefined
        }
    };
};

const updateBannerCreativeId = (
    state: CreativeState,
    action: UpdateBannerCreativeId
): CreativeState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasModalOpened: false
        }
    };
};

const removeCreative = (
    state: CreativeState,
    ids: EditorPlaceId[]
): CreativeState => {
    const result = ids.reduce((acc, id) => {
        return {
            [id]: {
                ...state[id],
                data: undefined
            }
        };
    }, {});

    return {
        ...state,
        ...result
    };
};

const updateBannerCreativeData = (
    state: CreativeState,
    action: UpdateBannerCreativeData
): CreativeState => {
    const prevCreative = state[action.bannerId];
    const creativeItem = action.creative;

    return {
        ...state,
        [action.bannerId]: {
            ...prevCreative,
            data: mapCreativeData(prevCreative.data, creativeItem)
        }
    };
};

const resetCreative = (
    state: CreativeState,
    creativeState: CreativeState
): CreativeState => {
    return {
        ...state,
        ...creativeState
    };
};

const updateCreative = (
    state: CreativeState,
    action: SetCreativeData
): CreativeState => {
    return resetCreative(state, action.creative);
};

const setSavedCreatives = (
    state: CreativeState,
    action: SaveBannersSuccess
): CreativeState => {
    const { added, cpmAdded } = action.result;
    let creativeData = state;

    [added, cpmAdded].forEach((addedBanners: SaveBannersResultData) => {
        if (!addedBanners.banners.length) {
            return;
        }

        addedBanners.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }

            const oldId = addedBanners.ids[index];
            const newId = String(addedBanner.id);

            creativeData = {
                ...omit(creativeData, oldId),
                [newId]: {
                    ...creativeData[oldId]
                }
            };
        });

        state = creativeData;
    });

    return state;
};

const clearCreatives = (
    state: CreativeState,
    action: ClearBanners
): CreativeState => {
    return Object.keys(action.bannersToClear).reduce(
        (accState, id) => {
            return {
                ...accState,
                [id]: {
                    ...accState[id],
                    data: undefined
                }
            };
        },
        { ...state }
    );
};

const deleteBanners = (
    state: CreativeState,
    action: DeleteBanners
): CreativeState => {
    return action.bannersIds.reduce((accState, id) => {
        return omit(accState, id);
    }, state);
};

const addNewTextBannersCreatives = (
    state: CreativeState,
    action: AddNewTextBannersAction,
    fullState: State
): CreativeState => {
    return {
        ...state,
        ...action.banners.reduce(
            (result: CreativeState, newBanner: NewBannerInputData) => {
                const creativeData = get(newBanner, 'bannerData.creative', {});

                if (creativeData) {
                    result[newBanner.bannerData.id] = { ...creativeData };
                }

                return result;
            },
            {}
        )
    };
};

const copyCreative = (
    state: CreativeState,
    action: CopyBanner
): CreativeState => {
    return {
        ...state,
        [action.newBannerId]: {
            ...state[action.bannerId]
        }
    };
};

const massUpdateBannersCreativeDataReduce = (
    state: CreativeState,
    action: MassUpdateBannerCreativeData
): CreativeState => {
    const prevCreative = state[action.bannerId];
    const additionalData = action.creatives.map(curCreative =>
        mapCreativeData(prevCreative.data, curCreative)
    );

    return {
        ...state,
        [action.bannerId]: {
            ...prevCreative,
            additionalData,
            // Нужно для отображения креатива
            data: additionalData[0]
        }
    };
};

export function creative(
    state: CreativeState = {},
    action: Action,
    fullState: State
): CreativeState {
    switch (action.type) {
        case ON_ACCEPT_MASS_ACTIONS_CPM_CREATIVE_BULK_REPLACEMENT_EDITOR:
            return resetCreative(state, {
                ...selectCpmCreativeDataByCreativeReplace(fullState),
                [action.massActionId]: {
                    ...state[action.massActionId],
                    additionalData: undefined,
                    data: undefined
                }
            });
        case CLOSE_MASS_ACTIONS_CPM_CREATIVE_BULK_REPLACEMENT_EDITOR:
            return removeCreative(state, [action.massActionId]);
        case OPEN_CREATIVE_CONSTRUCTOR:
            return openCreativeConstructor(state, action);
        case OPEN_CREATIVE_CONSTRUCTOR_PRESETS:
            return openCreativeConstructorPresets(state, action);
        case OPEN_CREATIVE_HTML5_CONSTRUCTOR:
            return openCreativeHtml5Constructor(state, action);
        case OPEN_CREATIVE_IDEAS:
            return openCreativeIdeas(state, action);
        case OPEN_CREATIVE_HTML5_ADD_CONSTRUCTOR:
            return openCreativeHtml5AddConstructor(state, action);
        case CLOSE_CREATIVE_CONSTRUCTOR:
            return closeConstructor(state, action);
        case UPDATE_BANNER_CREATIVE_ID:
            return updateBannerCreativeId(state, action);
        case UPDATE_BANNER_CREATIVE_DATA:
            return updateBannerCreativeData(state, action);
        case SET_CREATIVE_DATA:
            return updateCreative(state, action);
        case CLEAR_BANNERS:
            return clearCreatives(state, action);
        case SAVE_BANNERS_SUCCESS:
            return setSavedCreatives(state, action);
        case ADD_NEW_TEXT_BANNERS:
            return addNewTextBannersCreatives(state, action, fullState);
        case COPY_BANNER:
            return copyCreative(state, action);
        case DELETE_BANNERS:
            return deleteBanners(state, action);
        case MASS_UPDATE_BANNER_CREATIVE_DATA:
            return massUpdateBannersCreativeDataReduce(state, action);
        default:
            return state;
    }
}
